import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { render } from '@markbird/markdown';
import type { GetSiteResponse } from '@markbird/shared';
import { renderMermaid } from '../lib/mermaid';
import { renderKatex } from '../lib/katex';
import { resolveImagesInDom } from '../lib/images';
import { useTheme } from '../hooks/useTheme';
import { useT } from '../i18n/useT';
import { ReportDialog } from '../components/ReportDialog';
import { WIKILINK_RE } from '../lib/wikilinks';

/**
 * A published site: several notes at `/p/<site>`, one per page.
 *
 * It is the share viewer with two differences, and they are the two things
 * that make a set of notes a site rather than a pile of links: every page
 * carries the way to the others, and a `[[wiki link]]` to a page of the same
 * site is a link you can click. A link to something that was not published
 * stays plain text — a reader should never be offered a door that is not there.
 *
 * Everything else is deliberately the share viewer's: the same sanitizing
 * render, the same report footer, the same deletion link.
 */

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; site: GetSiteResponse };

/**
 * Turn `[[Title]]` into a link when the site has that page.
 *
 * Done on the rendered DOM, after sanitizing: nothing here builds HTML from
 * the document's text, so a page title cannot become markup.
 */
function linkWikiRefs(
  root: HTMLElement,
  slug: string,
  pages: { slug: string; title: string }[],
): void {
  const byTitle = new Map(pages.map((page) => [page.title.trim().toLowerCase(), page.slug]));
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.textContent && node.textContent.includes('[[')) texts.push(node as Text);
  }

  for (const text of texts) {
    // Code keeps its brackets: `[[x]]` in an example is an example.
    if (text.parentElement?.closest('code, pre')) continue;
    const source = text.textContent ?? '';
    const parts = document.createDocumentFragment();
    let last = 0;
    for (const match of source.matchAll(WIKILINK_RE)) {
      const [whole, target = '', alias] = match;
      const index = match.index ?? 0;
      parts.append(source.slice(last, index));
      last = index + whole.length;

      const page = byTitle.get(target.trim().toLowerCase());
      const label = alias ?? target;
      if (page) {
        const link = document.createElement('a');
        link.className = 'mb-site-link';
        link.href = `/p/${slug}/${page}`;
        link.textContent = label;
        parts.append(link);
      } else {
        parts.append(label);
      }
    }
    if (last === 0) continue;
    parts.append(source.slice(last));
    text.replaceWith(parts);
  }
}

export function SitePage() {
  useTheme();
  const t = useT();
  const { slug = '', page } = useParams<{ slug: string; page: string }>();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [reportOpen, setReportOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  /** The secret from the publishing link takes the whole site down. */
  const deleteSecret = useMemo(
    () => new URLSearchParams(window.location.hash.slice(1)).get('delete') ?? '',
    [],
  );
  const [deleteState, setDeleteState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');
  const [siteId, setSiteId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setState({ status: 'loading' });
      try {
        const url = page
          ? `/api/sites/by-slug/${encodeURIComponent(slug)}/${encodeURIComponent(page)}`
          : `/api/sites/by-slug/${encodeURIComponent(slug)}`;
        const res = await fetch(url);
        if (cancelled) return;
        if (res.status === 410) return setState({ status: 'error', message: t('site.expired') });
        if (!res.ok) return setState({ status: 'error', message: t('sp.invalid') });
        const site = (await res.json()) as GetSiteResponse;
        if (cancelled) return;
        setSiteId(site.id);
        setState({ status: 'ready', site });
      } catch {
        if (!cancelled) setState({ status: 'error', message: t('sp.invalid') });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, page]);

  const content = state.status === 'ready' ? state.site.page.content : '';
  const html = useMemo(() => render(content), [content]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || state.status !== 'ready') return;
    body.innerHTML = html;
    linkWikiRefs(body, state.site.slug, state.site.pages);
    void renderMermaid(body);
    void renderKatex(body);
    void resolveImagesInDom(body);
  }, [html, state]);

  const deleteSite = async () => {
    setDeleteState('busy');
    try {
      const res = await fetch(`/api/sites/${siteId ?? ''}`, {
        method: 'DELETE',
        headers: { 'x-delete-secret': deleteSecret },
      });
      if (res.ok || res.status === 404) {
        setDeleteState('done');
        setState({ status: 'error', message: t('site.deleted') });
      } else {
        setDeleteState('failed');
      }
    } catch {
      setDeleteState('failed');
    }
  };

  const current = state.status === 'ready' ? state.site.page.slug : '';

  return (
    <div className="mb-share-page mb-site-page">
      <header className="mb-share-header">
        <span className="mb-share-brand">markbird</span>
        {state.status === 'ready' && <span className="mb-site-title">{state.site.siteTitle}</span>}
        <span style={{ flex: 1 }} />
        <a className="mb-btn" href="/">
          {t('sp.open')}
        </a>
      </header>

      {deleteSecret && siteId && deleteState !== 'done' && (
        <div className="mb-share-delete-bar" role="region" aria-label={t('site.deleteTitle')}>
          <span>{deleteState === 'failed' ? t('sp.deleteFailed') : t('site.deleteHint')}</span>
          <button
            className="mb-btn mb-btn-danger"
            disabled={deleteState === 'busy'}
            onClick={() => void deleteSite()}
          >
            {deleteState === 'busy' ? t('sp.deleting') : t('site.deleteNow')}
          </button>
        </div>
      )}

      <main className="mb-share-main mb-site-main">
        {state.status === 'loading' && <p className="mb-share-note">{t('sp.loading')}</p>}
        {state.status === 'error' && (
          <div className="mb-share-note">
            <h1>{t('sp.notAvailable')}</h1>
            <p>{state.message}</p>
            <a className="mb-btn mb-btn-primary" href="/">
              {t('sp.goto')}
            </a>
          </div>
        )}
        {state.status === 'ready' && (
          <>
            <nav className="mb-site-nav" aria-label={t('site.pages')}>
              <ul>
                {state.site.pages.map((entry) => (
                  <li key={entry.slug}>
                    <Link
                      to={`/p/${state.site.slug}/${entry.slug}`}
                      aria-current={entry.slug === current ? 'page' : undefined}
                    >
                      {entry.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <article ref={bodyRef} className="markbird-preview markdown-preview" />
          </>
        )}
      </main>

      {state.status === 'ready' && (
        <footer className="mb-share-footer">
          <button type="button" className="mb-share-report" onClick={() => setReportOpen(true)}>
            {t('report.open')}
          </button>
        </footer>
      )}
      {/* Reported per page, not per site: what is wrong with one page is not
          wrong with the rest, and a takedown removes the page it is about. */}
      {reportOpen && state.status === 'ready' && (
        <ReportDialog
          target={{ token: state.site.page.token }}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}
